// A drop-in for react-router's useSearchParams whose setter builds on the URL as it is NOW, not on the one of the
// last render: react-router's setSearchParams(fn) starts from the params of the last render, so a second change made
// before the page re-rendered (typing, then at once clicking a control) wrote over the first one and lost it.
// A push that would not change the URL is no push: a view switch to what is already shown adds no empty history step.
import { useCallback } from 'react';
import { createSearchParams, useNavigate, useSearchParams as useRouterSearchParams, type NavigateOptions, type URLSearchParamsInit } from 'react-router';

export type SetParams = (next: URLSearchParamsInit | ((live: URLSearchParams) => URLSearchParams), options?: NavigateOptions) => void;

export function useSearchParams(): [URLSearchParams, SetParams] {
  const [params] = useRouterSearchParams();
  const navigate = useNavigate();
  const set = useCallback<SetParams>((next, options) => {
    const value = typeof next === 'function' ? next(new URLSearchParams(window.location.search)) : createSearchParams(next);
    const search = value.toString();
    if (!options?.replace && search === window.location.search.replace(/^\?/, '') && options?.state === undefined) return;
    void navigate({ search: search ? `?${search}` : '' }, options);
  }, [navigate]);
  return [params, set];
}
